import { useEffect, useRef, useState } from 'react';

// Returns an opacity/transition style pair that dips to 0 and eases back to 1 whenever
// `key` changes, so swapping artwork/text for a new track fades in instead of popping in.
export default function useFadeOnChange(key, duration = 300) {
  const [opacity, setOpacity] = useState(1);
  const [transition, setTransition] = useState('none');
  const prevKeyRef = useRef(key);
  const rafIdsRef = useRef([]);

  useEffect(() => {
    if (key === prevKeyRef.current) return;
    prevKeyRef.current = key;

    // The drop to 0 must be instant (no transition), otherwise the same eased transition
    // applies to it and it barely moves before the rise back to 1 reverses it a frame later.
    // Two rAFs guarantee the instant/invisible frame is actually painted before the eased
    // rise starts - a single rAF risks both updates landing in the same paint, which would
    // skip the dip entirely since the previously-painted opacity was already 1.
    setTransition('none');
    setOpacity(0);
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        setTransition(`opacity ${duration}ms ease`);
        setOpacity(1);
      });
      rafIdsRef.current.push(raf2);
    });
    rafIdsRef.current.push(raf1);

    return () => {
      rafIdsRef.current.forEach(cancelAnimationFrame);
      rafIdsRef.current = [];
    };
  }, [key, duration]);

  return { opacity, transition };
}
